/**
 * Modèles de messages et variables (architecture §1.6 ; présentation §07 et §08 ; RG-02).
 * Les corps des modèles sont des données versionnées (`message_template_versions`) ; le contrat fixe les codes,
 * le destinataire et les variables autorisées. Le moteur de rendu et les contrôles sont L15.
 */

export const SIGNATURE_CABINET = 'Cabinet MAAT' as const;

export type Destinataire = 'famille' | 'notaire' | 'organisme';

export const MODELES = [
  { code: 'A1', canal: 'whatsapp', destinataire: 'famille', libelle: 'Accueil d’un héritier' },
  { code: 'R1', canal: 'whatsapp', destinataire: 'famille', libelle: 'Relance douce' },
  { code: 'R2', canal: 'whatsapp', destinataire: 'famille', libelle: 'Pièce manquante' },
  { code: 'E1', canal: 'email', destinataire: 'famille', libelle: 'Récapitulatif officiel' },
  { code: 'E2', canal: 'email', destinataire: 'famille', libelle: 'Compte rendu après appel' },
  { code: 'N1', canal: 'email', destinataire: 'notaire', libelle: 'Transmission à la clerc de notaire' },
  { code: 'P1', canal: 'whatsapp', destinataire: 'famille', libelle: 'Quote-part avant signature' },
  { code: 'C1', canal: 'postal', destinataire: 'famille', libelle: 'Courrier d’annonce (tableau des vivants fini)' },
  { code: 'G1', canal: 'email', destinataire: 'organisme', libelle: 'Relance du greffe' },
] as const satisfies readonly { code: string; canal: 'whatsapp' | 'email' | 'postal'; destinataire: Destinataire; libelle: string }[];

export type CodeModele = (typeof MODELES)[number]['code'];

export function modele(code: CodeModele): (typeof MODELES)[number] {
  const m = MODELES.find((x) => x.code === code);
  if (!m) throw new Error(`Modèle inconnu : ${code}`);
  return m;
}

export interface VariableModele {
  cle: string;
  libelle: string;
  source: string;
  /** Interdite dans tout modèle destiné à la famille (RG-02 : le mandant n'est jamais nommé). */
  interditeFamille: boolean;
}

export const VARIABLES_MODELE = [
  { cle: 'prenom', libelle: 'Prénom du destinataire', source: 'persons.first_names', interditeFamille: false },
  { cle: 'nom', libelle: 'Nom du destinataire', source: 'persons.last_name', interditeFamille: false },
  { cle: 'civilite', libelle: 'Civilité (Madame, Monsieur)', source: 'persons.sex', interditeFamille: false },
  { cle: 'defunt', libelle: 'Prénom et nom du défunt', source: 'successions.deceased_person_id', interditeFamille: false },
  { cle: 'lien_avec_defunt', libelle: 'Lien du destinataire avec le défunt', source: 'relationships (calculé)', interditeFamille: false },
  { cle: 'reference_dossier', libelle: 'Référence du dossier', source: 'dossiers.reference', interditeFamille: false },
  { cle: 'commune_bien', libelle: 'Commune du bien', source: 'assets.commune', interditeFamille: false },
  { cle: 'lien_portail', libelle: 'Lien personnel vers le portail', source: 'portal_links (code court)', interditeFamille: false },
  { cle: 'piece_demandee', libelle: 'Pièce demandée', source: 'document_requirements.kind', interditeFamille: false },
  { cle: 'reste_du', libelle: 'Reste dû de la quote-part', source: 'installments (calculé)', interditeFamille: false },
  { cle: 'date_limite', libelle: 'Date limite de collecte', source: 'collection_schedules.deadline', interditeFamille: false },
  {
    cle: 'date_signature',
    libelle: 'Date de signature chez le notaire',
    source: 'collection_schedules.signature_date',
    interditeFamille: false,
  },
  { cle: 'etude', libelle: 'Nom de l’étude notariale', source: 'etudes.name', interditeFamille: false },
  { cle: 'clerc', libelle: 'Nom de la clerc', source: 'etude_members (fonction clerc)', interditeFamille: false },
  { cle: 'expediteur', libelle: 'Prénom de la personne qui écrit', source: 'users.display_name', interditeFamille: false },
  { cle: 'signature', libelle: 'Signature « Cabinet MAAT »', source: 'constante SIGNATURE_CABINET', interditeFamille: false },
  { cle: 'mandant', libelle: 'Nom du mandant', source: 'dossiers.mandant_person_id', interditeFamille: true },
] as const satisfies readonly VariableModele[];

export type CleVariable = (typeof VARIABLES_MODELE)[number]['cle'];

/** Variables utilisables dans un modèle : toutes, sauf celles interdites pour la famille si le modèle lui est destiné. */
export function variablesAutorisees(code: CodeModele): CleVariable[] {
  const pourFamille = modele(code).destinataire === 'famille';
  return VARIABLES_MODELE.filter((v) => !(pourFamille && v.interditeFamille)).map((v) => v.cle);
}

/** Variables `{cle}` présentes dans un corps de modèle, dans l'ordre d'apparition. */
export const variablesDuTexte = (texte: string): string[] => [...texte.matchAll(/\{([a-z_]+)\}/g)].map((m) => m[1] as string);
